import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    FiHome,
    FiUser,
    FiBarChart2,
    FiUsers,
    FiPieChart,
} from 'react-icons/fi';

const navItems = [
    {
        id: 'home',
        icon: FiHome,
        label: 'Home',
        path: '/',
        accent: '#6366f1',
    },
    {
        id: 'team',
        icon: FiUsers,
        label: 'Team',
        path: '/team',
        accent: '#ec4899',
    },
    {
        id: 'trade',
        icon: FiBarChart2,
        label: 'Market',
        path: '/trade-list',
        accent: '#3b82f6',
    },
    {
        id: 'r-trade',
        icon: FiPieChart,
        label: 'R-Trade',
        path: '/recovery-trade',
        accent: '#10b981',
    },
    {
        id: 'profile',
        icon: FiUser,
        label: 'Profile',
        path: '/profile',
        accent: '#f59e0b',
    },
];

const themeConfig = {
    light: {
        barBg: 'rgba(255, 255, 255, 0.92)',
        barBorder: 'rgba(99, 102, 241, 0.12)',
        barShadow:
            '0 8px 32px rgba(99, 102, 241, 0.15), 0 2px 8px rgba(0, 0, 0, 0.06)',
        inactiveLabel: '#94a3b8',
    },
    dark: {
        barBg: 'rgba(15, 23, 42, 0.88)',
        barBorder: 'rgba(129, 140, 248, 0.2)',
        barShadow:
            '0 8px 32px rgba(0, 0, 0, 0.45), 0 0 24px rgba(99, 102, 241, 0.12)',
        inactiveLabel: '#64748b',
    },
};

const BottomNavigation = ({ theme }) => {
    const location = useLocation();
    const t = themeConfig[theme] || themeConfig.dark;
    const isDark = theme === 'dark';

    return (
        <motion.nav
            className="position-fixed bottom-0 start-50 translate-middle-x z-3"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            style={{
                width: 'min(400px, 100vw)',
                marginBottom: 0,
                bottom: 0,
                padding: 0,
                borderRadius: '20px 20px 0 0',
                overflow: 'hidden',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                background: t.barBg,
                border: `1px solid ${t.barBorder}`,
                boxShadow: t.barShadow,
            }}
        >
            <div
                className="d-flex align-items-stretch justify-content-between"
                style={{ gap: 0, minHeight: '58px' }}
            >
                {navItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;

                    return (
                        <div
                            key={item.id}
                            className="flex-fill position-relative"
                            style={{ minWidth: 0 }}
                        >
                            <Link
                                to={item.path}
                                className="d-flex flex-column align-items-center justify-content-center text-decoration-none border-0 w-100 h-100 p-0"
                                style={{
                                    padding: 0,
                                    position: 'relative',
                                    zIndex: 1,
                                }}
                            >
                                {isActive && (
                                    <motion.div
                                        layoutId="nav-active-indicator"
                                        className="position-absolute"
                                        style={{
                                            top: 0,
                                            left: 'calc(50% - 16px)',
                                            width: 32,
                                            height: 3,
                                            borderRadius: '0 0 4px 4px',
                                            background: item.accent,
                                        }}
                                        transition={{
                                            type: 'spring',
                                            stiffness: 380,
                                            damping: 30,
                                        }}
                                    />
                                )}

                                <motion.div
                                    className="position-relative d-flex align-items-center justify-content-center"
                                    style={{
                                        width: 34,
                                        height: 34,
                                        borderRadius: '12px',
                                        background: isActive
                                            ? isDark
                                                ? `${item.accent}22`
                                                : `${item.accent}14`
                                            : 'transparent',
                                        border: isActive
                                            ? `1.5px solid ${item.accent}${isDark ? '55' : '40'}`
                                            : '1.5px solid transparent',
                                    }}
                                    animate={{
                                        scale: isActive ? 1.06 : 1,
                                        y: isActive ? -1 : 0,
                                    }}
                                    whileTap={{ scale: 0.9 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <Icon
                                        size={isActive ? 20 : 18}
                                        color={item.accent}
                                        style={{
                                            opacity: isActive ? 1 : 0.75,
                                        }}
                                    />
                                </motion.div>

                                <motion.span
                                    className="position-relative text-truncate w-100 text-center"
                                    style={{
                                        fontSize: '0.65rem',
                                        fontWeight: isActive ? 700 : 500,
                                        letterSpacing: '0.02em',
                                        marginTop: 0,
                                        lineHeight: 1.2,
                                        color: isActive
                                            ? item.accent
                                            : t.inactiveLabel,
                                        maxWidth: '100%',
                                        padding: 0,
                                    }}
                                    animate={{
                                        opacity: isActive ? 1 : 0.75,
                                    }}
                                >
                                    {item.label}
                                </motion.span>
                            </Link>
                        </div>
                    );
                })}
            </div>
        </motion.nav>
    );
};

export default BottomNavigation;
